// @file Common types that we use
import { RGBString } from '@@/bits/color'
import type { UserRole } from '@@/bits/user_model'
import type {
  AccessibilitySettingPreference,
  ApiErrorCode,
  ClassRole,
  ClassroomPlanPricingExperimentVariant,
  CommandLevel,
  ContentModerationCategory,
  ContentModerationMode,
  DiscussionStyle,
  Gradable,
  GradeCategory,
  LibraryMembershipRole,
  LibraryType,
  LmsSyncState,
  ModerationCategories,
  NotificationChannelType,
  NotificationType,
  PostSize,
  SchoolPermissionString,
  SchoolStudentPermissionBoolean,
  SchoolTeacherPermissionBoolean,
  ShownCursorType,
  SmartSuggestionsExperimentVariant,
  SnackbarLoadingStatus,
  SponsoredUserStatus,
  TeamPermissionString,
  UserAccountType,
  WallAccessPrivacyType,
  WallAccessRight,
  WallAccessRole,
  WallCollaboratorStatus,
  WallGradeCalculationMethods,
  WallPermission,
  WallPrivacy,
  WallPrivacyOption,
  WallPrivacyOptionV2,
  WallVizName,
  WallWishGroupByTypes,
  WallWishSortByTypes,
} from '@@/enums'
import type {
  Card,
  ContentProtection,
  Invoice,
  JsonAPIResource,
  Link,
  PlanDisplay,
  Reaction,
  ReactionType,
  WallBackgroundType,
} from '@padlet/arvo'
import type { TLPage, TLRecord } from 'tldraw'
import type { CSSProperties } from 'vue'

type Id = number
type HashId = string
type UserId = Id
type UserGroupId = Id
type TenantId = Id
type WallId = Id
type FolderId = Id
type LibraryId = Id
type LibraryMembershipId = Id
type PrivacyPolicyId = Id
type OnboardingFlowId = Id
// TODO: ENG-28586 eventually remove string type.
// For now we are using WipStore's doc_id, which is a string, but eventually this will be a number id from the actual discussions table
type CampusDiscussionId = Id | string

// Be careful when using this on recursive types
type DeepPartial<T> = T extends object ? { [P in keyof T]?: DeepPartial<T[P]> } : T

interface DeviceDetails {
  // Gadgets
  ipad: boolean
  ipod: boolean
  iphone: boolean
  iphoneX: boolean

  // OSes
  ios: boolean
  android: boolean
  windowsPhone: boolean
  mac: boolean
  windows: boolean
  webos: boolean
  blackberry: boolean
  windowsTouch: boolean

  // Browsers
  edge: boolean
  chrome: boolean
  firefox: boolean
  ie: boolean
  webkit: boolean
  safari: boolean
  ie11: boolean
  ie10: boolean
  version: number

  // Platform type
  mobile: boolean
  desktop: boolean
  supported: boolean

  // Capabilities
  touchable: boolean
  mousable: boolean
  retina: boolean
  clickGesture: string
  dragFile: boolean
  cookies: boolean

  // Variants
  tablet: boolean
  phone: boolean
  iosApp: boolean
  androidApp: boolean
  app: boolean
  desktopApp: boolean
  nwjs: boolean
  electronApp: boolean

  // Push
  safariPush: boolean
  webPush: boolean
  browserPush: boolean
}

interface UserQuota {
  walls_used: number
  walls_limit: number
  advertized_walls_limit: number
  can_make: boolean
}

interface User {
  id: UserId
  hashid?: HashId
  tenant_id: TenantId
  paying: boolean
  quota: UserQuota
  role: UserRole
  wall_role?: 'owner' | 'admin' | 'teacher' | 'student' | null
  lang: string
  created_at: string
  updated_at: string
  registered_at: string | null
  defaultAvatar?: string
  avatar?: string
  avatar_dominant_rgb: number[]
  paying_for_self_only?: boolean
  membership_tier?: string
  period?: string | null
  name?: string
  username?: string
  display_name?: string
  email?: string
  short_name?: string
  registered?: boolean
  profile_url?: string
  bio?: string
  status?: string
  isBetaFeaturePreviewEnabled?: boolean
  canUpdateEmail?: boolean
  canUpdateName?: boolean
  canUpdateUsername?: boolean
  tenant_type: string
  hasRecentPadlets?: boolean
  defaultLibraryId?: string
  visible?: boolean
  // Specifically used to trigger snackbar when redirected to dashboard settings after changing and verifying email
  isEmailRecentlyUpdatedAndVerified?: boolean
  // Specifically used to trigger snackbar when redirected to dashboard after login and verifying email
  isEmailRecentlyLoggedInAndVerified?: boolean
  // true if user's email address is verified, or if the user logs in via SSO
  is_verified?: boolean
  account_type?: UserAccountType
  days_before_freeze: number
  personal_pricing_plan_variant?: string
  classroom_plan_pricing_experiment_variant?: ClassroomPlanPricingExperimentVariant
  smart_suggestions_experiment_variant?: SmartSuggestionsExperimentVariant
  is_archive_enabled: boolean
  hasCreatedPadletBefore?: boolean
  emailMatchesNonNativeUser?: boolean
  is_emailless_library_user?: boolean

  // Accessibility preferences
  is_keyboard_shortcuts_enabled?: boolean
  is_app_messages_auto_dismissed?: boolean
  high_contrast_mode_preference?: AccessibilitySettingPreference
  reduced_motion_preference?: AccessibilitySettingPreference
  trial_days_left?: number
  is_trial_expired?: boolean
  trial_duration_days?: number
  eligible_for_self_serve_trial?: boolean
}

interface UserCamelCase {
  id: UserId
  tenant_id: TenantId
  paying: boolean
  quota: UserQuota
  role: UserRole
  lang: string
  createdAt: Date
  registeredAt: Date | null
  defaultAvatar?: string
  avatar?: string
  payingForSelfOnly?: boolean
  membershipTier?: string
  period?: string | null
  name?: string
  username?: string
  displayName?: string
  email?: string
  shortName?: string
  registered?: boolean
  profileUrl?: string
  bio?: string
  isBetaFeaturePreviewEnabled?: boolean
  canShowEmail?: boolean
  canUpdateEmail?: boolean
  canUpdateName?: boolean
  canUpdateAvatar?: boolean
  canUpdateUsername?: boolean
  tenantType: string
  hasRecentPadlets?: boolean
  defaultLibraryId?: string
  visible?: boolean
  // Specifically used to trigger snackbar when redirected to dashboard settings after changing and verifying email
  isEmailRecentlyUpdatedAndVerified?: boolean
  // Specifically used to trigger snackbar when redirected to dashboard after login and verifying email
  isEmailRecentlyLoggedInAndVerified?: boolean
  // true if user's email address is verified, or if the user logs in via SSO
  isVerified?: boolean
  accountType?: UserAccountType
  isNative?: boolean
  days_before_freeze: number

  // Accessibility preferences
  isKeyboardShortcutsEnabled?: boolean
  isAppMessagesAutoDismissed?: boolean
  highContrastModePreference?: AccessibilitySettingPreference
  reducedMotionPreference?: AccessibilitySettingPreference
}

interface UserWithHashId extends User {
  hashid: string
}

interface UserOptions {
  canAddLibrary?: boolean
  canManagePeople?: boolean
  canMakeInLibrary?: boolean
  canMakeWallInTenant?: boolean
  canViewGallery?: boolean
  linkedGoogleDriveEmail?: string
  linkedMicrosoftOneDriveEmail?: string
  canSearchWallsOnDashboard?: boolean
  canAccessTransferPadletsTool?: boolean
  isTeacher?: boolean
}

type AccountType = 'user' | 'library'
type AccountKey = { type: 'library'; id: LibraryId } | { type: 'user'; id: UserId }
type SharedCollectionAccountKey = AccountKey

interface TenantAssets {
  home_logo?: string
}

interface TenantToLibraryMigrationState {
  completionWeight: number
  libraryId: number
  tenantId: number
}

interface FreezeBannerState {
  days_until_freeze: number
  role: string
  free_trial: boolean
}

interface Tenant {
  id: TenantId
  assets: Partial<TenantAssets> | null
  domain_name: string
  membership: string
  name: string
  type?: string
  alternate_user_domains?: string
  auto_create_accounts?: boolean
  enable_google_app_licensing?: boolean | null
  enable_external_rostering?: boolean | null
  enable_oneroster?: boolean | null
  google_app_licensing_order_id?: string | null
  google_app_licensing_redeem_uri?: string | null
  invalid_email_domains?: string[]
  owner_email_domain?: string | null
  enable_google_login?: boolean | null
  enable_office365_login?: boolean | null
  enable_classlink_login?: boolean | null
  enable_clever_login?: boolean | null
  enable_saml_login?: boolean
  classlink_tenant_id?: string | null
  clever_district_id?: string | null
  is_lti_ready?: boolean
  is_native?: boolean
  ownerLink: string
  hide_password_login_if_sso_enabled: boolean
  dashboard_settings_visibility_toggles: {
    password: boolean
  }
  lti_enabled?: boolean
  enable_lti_user_group_sync?: boolean
  lms_user_group_sync_config_enabled?: boolean
  settings?: {
    permissions?: {
      // student wall permissions
      studentDefaultWallPrivacy?: WallPrivacy
      studentDefaultWallPermission?: WallPermission
      canStudentsMakeWalls?: boolean
      canStudentsMakePrivateWalls?: boolean
      canStudentsMakeOrgWideHiddenWalls?: boolean
      canStudentsMakeOrgWideDashboardWalls?: boolean
      canStudentsMakePasswordWalls?: boolean
      canStudentsMakeSecretWalls?: boolean
      // teacher wall permissions
      teacherDefaultWallPrivacy?: WallPrivacy
      teacherDefaultWallPermission?: WallPermission
      canTeachersMakeWalls?: boolean
      canTeachersMakePrivateWalls?: boolean
      canTeachersMakeOrgWideHiddenWalls?: boolean
      canTeachersMakeOrgWideDashboardWalls?: boolean
      canTeachersMakePasswordWalls?: boolean
      canTeachersMakeSecretWalls?: boolean
      // student permission
      canStudentsViewGallery?: boolean
      // teacher permissions
      canTeachersAddNewUsers?: boolean
      canTeachersEditUsers?: boolean
      // briefcase permissions
      briefcaseDefaultWallPrivacy?: WallPrivacy
      briefcaseDefaultWallPermission?: WallPermission
    }
  }
  is_frozen?: boolean
}

type TenantParamsKeys =
  | 'assets'
  | 'domain_name'
  | 'name'
  | 'alternate_user_domains'
  | 'auto_create_accounts'
  | 'enable_google_login'
  | 'enable_office365_login'
  | 'enable_classlink_login'
  | 'enable_google_app_licensing'
  | 'enable_external_rostering'
  | 'enable_oneroster'
  | 'google_app_licensing_order_id'
  | 'google_app_licensing_redeem_uri'
  | 'enable_clever_login'
  | 'hide_password_login_if_sso_enabled'
  | 'classlink_tenant_id'
  | 'clever_district_id'

type TenantParams = Pick<Tenant, TenantParamsKeys>

interface TenantInviteLinks {
  id?: TenantId
  studentLink?: string
  teacherLink?: string
  adminLink?: string
  isStudentLinkCodeEnabled?: boolean
  isTeacherLinkCodeEnabled?: boolean
  isAdminLinkCodeEnabled?: boolean
}

interface TenantInviteLinksParams {
  generateStudentLinkCode: boolean
  generateTeacherLinkCode: boolean
  generateAdminLinkCode: boolean
  isStudentLinkCodeEnabled: boolean
  isTeacherLinkCodeEnabled: boolean
  isAdminLinkCodeEnabled: boolean
}

interface TenantAnalytics {
  orgAnalyticsEmbedLink: string
  monthlyAnalyticsEmbedLink: string | null
  orgUserAnalyticsEmbedLink: string
}

interface LibraryAnalytics {
  libraryAnalyticsEmbedLink: string
  monthlyAnalyticsEmbedLink: string | null
  libraryUserAnalyticsEmbedLink: string
}

interface LibraryAttributeCheck {
  valid: boolean
  slugError?: string
  nameError?: string
}

interface DomainValidation {
  result: boolean
  subdomain?: string
  code?: ApiErrorCode
}

type WallViz =
  | 'free'
  | 'grid'
  | 'matrix'
  | 'shelf'
  | 'stream'
  | 'map'
  | 'timeline'
  | 'table'
  | 'columns'
  | 'timeline_v2'
  | 'whiteboard'

type WallVizNameType = keyof typeof WallVizName

interface OnboardingWallViz {
  name: WallVizNameType
  value: WallViz
  imagePath: string
  sectionsImagePath: string
  description: string
}

interface WallBackground {
  url: string
  thumbnail_url: string
  palette_order: number
  width: number
  height: number
  fill: 'cover' | 'tile' | 'auto' | 'contain' | 'undefined'
  effects: string[]
  is_full_page: boolean
  luminance_name: 'light' | 'dark'
  dominant_color: number[]
  dominant_color_as_rgb: RGBString
  primary_color: number[] | null
  primary_color_as_rgb: RGBString | null
  primary_text_color: 'white' | 'black' | null
  accent_color: number[] | null
  accent_color_as_rgb: RGBString | null
  accent_text_color: 'white' | 'black' | null
  is_custom?: boolean
  name?: string
  // for backward compatibility
  effect?: string | null
  is_title_bar_transparent_by_default?: boolean
  is_title_bar_transparent?: boolean
  type: WallBackgroundType
}

interface Wall {
  advertized_upload_limit: number
  id: WallId
  hashid: HashId
  library_id?: LibraryId | null
  name?: string
  description?: string
  namespace?: string
  address: string
  archived_at?: string | null
  created_at: string
  updated_at: string
  last_present_at?: string | null
  days_till_deletion?: number | null
  title: string
  viz: WallViz
  portrait?: string
  post_size?: PostSize
  builder: {
    id: UserId
    avatar: string
    display_name: string
    short_name: string
    username: string
    name?: string
    role: UserRole
    registered: boolean
    paying: boolean
    days_before_freeze: number
  }
  background: WallBackground
  thumbnail: {
    dominant_color: number[]
    dominant_color_as_rgb: string
    height: number | null
    width: number | null
    url: string
  }
  links: WallLinks
  is_private?: boolean
  is_organization_restricted?: boolean
  frozen?: boolean
  font_id?: number
  color_scheme?: string
  audio_length_limit?: number
  video_length_limit?: number
  whiteboard_frame_limit?: number
  is_reactable: boolean
  is_commentable: boolean
  readonly upload_limit: number
  tenant_id: number
  show_author: boolean
  reaction_data: WallReactionData | null
  wish_arrangement: WishArrangement
  is_title_bar_transparent: boolean
  cover_wish_id?: number | null
  is_template?: boolean
  content_moderation: ContentModerationMode
  filter_profanity?: boolean
  new_wish_loc?: 'top' | 'bottom'
  theme_id?: number
  require_approval?: boolean
  is_remakeable?: boolean
  content_protection?: ContentProtection
  breakout_links_visited?: boolean
  freeze_banner_information?: FreezeBannerState | null
  reactions_migrated?: boolean
}

interface WishArrangement {
  default_section_id: number | null
  group_by: WallWishGroupBy | null
  sort_by: WallWishSortByTypes | null
  is_sectionable: boolean
  version: number
}
type AttachmentSettings = boolean | Record<string, boolean>
type RequiredFields = {
  subject: boolean
  body: boolean
  attachment: boolean | Record<string, boolean>
}
export type VideoLengthLimit = null | number // number of seconds
interface WallPostProperties {
  subject_placeholder: string | null
  body_placeholder: string | null
  subject_enabled: boolean
  attachment_settings: AttachmentSettings
  attachment_text: string | null
  body_enabled: boolean
  color_enabled: boolean
  location_enabled: boolean
  ar_lenses_enabled: boolean

  required_fields: RequiredFields
  video_length_limit: VideoLengthLimit
}

// keep these in sync with the backend in services/rails/app/services/wall_post_properties/custom_post_date_property.rb
type DateDisplayFormat = 'local' | 'friendly' | 'mmddyy' | 'ddmmyy' | 'iso' | 'unix'
type DateDisplayAs = 'dayMonthYear' | 'monthYear' | 'year'
type NumberDisplayFormat = 'local' | 'comma_period' | 'period_comma' | 'space_comma' | 'space_period' | 'custom'
type NumberDecimalPlaces = number | 'as_entered'
type CustomPostPropertyDataType =
  | 'text'
  | 'date'
  | 'number'
  | 'link_button'
  | 'single_select'
  | 'email'
  | 'url'
  | 'phone_number'
  | 'user'
  | 'rating'
  | 'vote'

type WallCustomPostProperty =
  | WallCustomPostDateProperty
  | WallCustomPostTextProperty
  | WallCustomPostNumberProperty
  | WallCustomPostLinkButtonProperty
  | WallCustomPostSingleSelectProperty
  | WallCustomPostEmailProperty
  | WallCustomPostUrlProperty
  | WallCustomPostPhoneNumberProperty
  | WallCustomPostUserProperty
  | WallCustomPostRatingProperty
  | WallCustomPostVoteProperty
interface WallCustomPostPropertyBase {
  id?: string // id of a custom property is a random string not a number
  name: string
  data_type: CustomPostPropertyDataType
  sort_index: number
  required: boolean
}

export interface WallCustomPostTextProperty extends WallCustomPostPropertyBase {
  data_type: 'text'
}

export interface WallCustomPostUrlProperty extends WallCustomPostPropertyBase {
  data_type: 'url'
}

export interface WallCustomPostPhoneNumberProperty extends WallCustomPostPropertyBase {
  data_type: 'phone_number'
}

export interface WallCustomPostUserProperty extends WallCustomPostPropertyBase {
  data_type: 'user'
}

export interface WallCustomPostDateProperty extends WallCustomPostPropertyBase {
  data_type: 'date'
  date_display_format: DateDisplayFormat
  show_time: boolean
  display_as: DateDisplayAs
}

export interface WallCustomPostNumberProperty extends WallCustomPostPropertyBase {
  data_type: 'number'
  number_display_format: NumberDisplayFormat
  decimal_places: NumberDecimalPlaces
  thousand_separators: boolean | null
}

export interface WallCustomPostLinkButtonProperty extends WallCustomPostPropertyBase {
  data_type: 'link_button'
}

export interface WallCustomPostSingleSelectProperty extends WallCustomPostPropertyBase {
  data_type: 'single_select'
  selection_options: SingleSelectOption[]
}

export interface WallCustomPostEmailProperty extends WallCustomPostPropertyBase {
  data_type: 'email'
}

type RatingDisplayFormat = 'star' | 'number'
type WhoCanRate = 'admins_only' | 'post_author_and_admins' | 'post_author' | 'commenters'
export interface WallCustomPostRatingProperty extends WallCustomPostPropertyBase {
  data_type: 'rating'
  rating_display_format: RatingDisplayFormat
  who_can_rate: WhoCanRate
  max_rating: number
}

type VoteType = 'upvotes_and_downvotes' | 'upvotes_only'
type WhoCanVote = 'admins_only' | 'post_author_and_admins' | 'post_author' | 'commenters'
export interface WallCustomPostVoteProperty extends WallCustomPostPropertyBase {
  data_type: 'vote'
  vote_type: VoteType
  who_can_vote: WhoCanVote
}

// #region - Custom Property Response Types

export interface CustomPropertyResponse {
  id: number
  hashid: string
  wall_id: number
  wish_id: number
  user_id: number
  custom_property_id: string
  int_value: number
  created_at: string
  updated_at: string
}

export interface AggregatedCustomPropertyResponse {
  sum: number
  count: number
}

export interface AggregatedCustomPropertyResponses {
  [wishId: string]: {
    [propertyId: string]: AggregatedCustomPropertyResponse
  }
}

export interface UserCustomPropertyResponses {
  [wishId: string]: {
    [propertyId: string]: CustomPropertyResponse
  }
}

export interface CustomPropertyResponsesData {
  aggregated_responses: AggregatedCustomPropertyResponses
  user_responses: UserCustomPropertyResponses
}

// #endregion - Custom Property Response Types

export interface SingleSelectOption {
  sort_index: number
  default: boolean
  name: string
  id?: string
}

interface WallTemplate {
  id: WallId
  name?: string
  description?: string
  title: string
  thumbnail: {
    height: number
    width: number
    url: string
    dominantColor: number[]
    dominantColorAsRgb: string
  }
}

type FilterGroup = 'grade' | 'category'

type TemplateFilters = {
  [group in FilterGroup]: Record<string, boolean>
}

interface WallGalleryTemplate {
  id: WallId
  name?: string
  description?: string
  title: string
  thumbnail: {
    height: number
    width: number
    url: string
    dominantColor: number[]
    dominantColorAsRgb: string
  }
  galleryTemplateId: string
  category?: string[]
  audience: string
  grades?: string[]
  previewPadletUrl: string
  relatedTemplateIds: string[]
  // TODO: change backend to send data in camel case
  exampleGalleryImages: Array<{
    image_height: number
    image_id: number
    image_url: string
    image_width: number
  }>
  templateCreator?: string
  publicKey: string
}

interface WallSuggestedTemplatesData {
  totalNumberOfGalleryTemplateWallsCreated: number
  recentGalleryTemplates: string[]
  suggestedTemplateIds: string[]
}

interface WallCamelCase {
  id: WallId
  hashid: HashId
  description?: string
  libraryId: LibraryId | null
  name: string
  namespace: string
  address: string
  archivedAt: string | null
  createdAt: string
  updatedAt: string
  lastPresentAt: string | null
  daysTillDeletion?: number | null
  title: string
  viz: WallViz
  collaboratorRight: number
  isInRecents: boolean
  builder: {
    id: UserId
    avatar: string
    displayName: string
    username: string
  }
  background: {
    dominantColor: number[]
    dominantColorAsRgb: string
    fill: string
    height?: number
    width?: number
    url: string
    effect: string | null
    luminanceName: 'light' | 'dark'
  }
  thumbnail: {
    dominantColor: number[]
    dominantColorAsRgb: string
    height: number | null
    width: number | null
    url: string
  }
  links: {
    show: string
    permanent: string
  }
  isTemplate?: boolean
  frozen?: boolean
  readonly uploadLimit: number
  videoLengthLimit: VideoLengthLimit
}

type EmojiPreset = 'all' | 'heart' | 'thumbs_up' | 'classic' | 'off'
interface WallReactionData {
  type: ReactionType
  max_value?: number | null
  emoji_preset?: EmojiPreset | null
  allowed_emojis?: string[] | null
}
interface Folder {
  id: FolderId
  name: string
  // TODO ENG-4489 remove snake case field after removing gql client
  walls_count?: number
  wallsCount?: number
  icon?: string
}

export enum UserGroupType {
  CLASS = 'class',
}
interface UserGroup {
  id: UserGroupId
  hashid?: string
  name: string
  icon?: string
  libraryId?: LibraryId
  thmbsGraphic?: string
  groupType?: UserGroupType
  grade?: string
  subject?: string
  allowStudentAddPadlet?: boolean
  allowStudentAddDiscussion?: boolean
  currentUserClassRole?: ClassRole.Teacher | ClassRole.Student
}

// Attributes that can be updated for a user group
interface UpdateUserGroupAttributes {
  name?: string
  grade?: string
  subject?: string
  icon?: string
  allowStudentAddPadlet?: boolean
  allowStudentAddDiscussion?: boolean
  allowStudentCreateDiscussion?: boolean
}

// Response from UserGroups API with full details
interface UserGroupDetailsApiResponse {
  id: UserGroupId
  name: string
  icon?: string
  grade?: string
  subject?: string
  externalSyncService?: string
  allowStudentAddPadlet?: boolean
  allowStudentAddDiscussion?: boolean
  isOwner?: boolean
  currentUserClassRole?: ClassRole.Teacher | ClassRole.Student
}

// Response from UserGroups member-related API calls
interface UserGroupMemberApiResponse {
  members?: Array<{
    id: number
    name: string
    username: string
    avatar: string
    classRole: ClassRole.Teacher | ClassRole.Student
    isOwner: boolean
  }>
  results?: Array<{
    id: number
    name: string
    username: string
    avatar: string
    classRole: ClassRole.Teacher | ClassRole.Student
  }>
  success?: boolean
}

interface Theme {
  id: number
  name: string
  full_name: string
  color_scheme: 'light' | 'dark'
  font_id: number
}

interface PinDropSuggestion {
  locationName: string
  placeId: string
  types?
  zoom: number
}

type Cid = string
type PostColor = 'red' | 'orange' | 'green' | 'blue' | 'purple' | undefined | null
type PostColorId = 'default' | 'red' | 'orange' | 'green' | 'blue' | 'purple'
interface PostPositionMap {
  top?: number | null
  left?: number | null
}

interface Point {
  longitude: number
  latitude: number
}

export interface PollChoice {
  choice_text: string
  poll_choice_id: Id
  total_votes: number
  user_voted: boolean
}

export interface PollResult extends PollChoice {
  percentage: number
  pollWinner: boolean
}

export interface Poll {
  type: 'poll'
  poll_id: Id
  user_voted?: boolean
  question: string
  poll_choices: PollChoice[]
  closed_at?: string | null
  edit_poll?: boolean
  voter_id: Id
}

// Attachment props can have various formats.
// We can be flexible for now until we have
// a better idea of how it should looks like.
type AttachmentProps = Record<string, any> // eslint-disable-line @typescript-eslint/no-explicit-any

interface WishContent {
  attachment_props: AttachmentProps | null
  is_processed?: boolean
}

interface WishAttachment {
  url: string
  id?: number
  alt?: string
  signed_url?: string
  hotlink?: string

  // Uploading states
  isUploadingFile?: boolean
  progress?: number
  fileBeingUploaded?: File
  uploadId?: string
}

interface PostAttributes {
  cid?: Cid
  id?: Id
  hashid?: HashId
  subject?: string
  body?: string
  attachment?: string
  attachments?: WishAttachment[]
  attachment_link?: Link | null
  attachment_caption?: string | null
  file?: File | null
  published?: boolean
  width?: number
  wallSectionId?: number
  wall_section_hashid?: string
  sortIndex?: number
  wall_section_id?: number
  created_at?: Date | string
  updated_at?: Date | string
  content_updated_at?: Date | string
  author_id?: number
  author_hashid?: HashId
  author?: User
  wall_id?: number
  wall_hashid?: HashId
  permalink?: string
  sort_index?: number
  rank?: string | null
  position?: PostPositionMap
  top?: number | null
  left?: number | null
  color?: PostColor
  row_id?: number | null
  col_id?: number | null
  rowCid?: string
  colCid?: string
  row_span?: number
  col_span?: number
  locationPoint?: Point
  location_point?: Point | Record<PropertyKey, never>
  locationName?: string
  location_name?: string | null
  is_content_hidden?: boolean
  wish_content?: WishContent | null
  scheduled_at?: string | null
  published_at?: string
  moderation_violations?: ModerationCategories[] | null
  pinned_at?: string | null
  custom_properties?: PostCustomProperties | null
}

interface UserFieldValue {
  id: number
  name?: string
  username?: string
}
type VoteOrRatingFieldValue = {
  sum?: number
  count?: number
  user_response?: number
}
type PostCustomPropertyValue = string | number | UserFieldValue | null | undefined | VoteOrRatingFieldValue // TODO: this type will be expanded over time
type PostCustomProperties = Record<string, PostCustomPropertyValue>

interface UpdatePostArgs extends PostAttributes {
  gap_size?: number | null
  group_by_changed?: boolean
}

interface MapPostAttributes extends PostAttributes {
  location_point: Point
  location_name: string
}

interface FileAttributes {
  name: string
  size: number | null
  isGoogleDriveFile: boolean
}

interface UpdateDraftAttributes extends PostAttributes {
  draftCreatedAt?: number
  minimizedPlaceholderTitle?: string
  uploadingFile?: File | FileAttributes | null
  uploadProgress?: number
  maxFileUploadSize?: number
  shouldAutoPublish?: boolean
}

interface Post extends PostAttributes {
  cid: Cid
}

interface DraftPost extends Post {
  // timestamp generated using Date.now()
  draftCreatedAt?: number
  minimizedPlaceholderTitle?: string
  uploadingFile?: File | null
  uploadProgress?: number
  maxFileUploadSize?: number
  shouldAutoPublish?: boolean
  hasCustomPropertyErrors?: boolean
  shouldStartEditing?: boolean
  uploadError?: string
}

type CommentTempId = string
type CommentId = Id | CommentTempId
interface Comment {
  id: CommentId
  hashid: HashId
  wish_id: Id
  user_id?: Id
  user_hashid?: HashId
  body: string
  html_body?: string
  attachment?: string | null
  attachment_signed_url?: string
  user: User
  created_at: string
  updated_at: string
  moderation_violations?: ModerationCategories[]
  is_awaiting_approval?: boolean
  published_at?: string
  submitted_at?: string
}

type CommentAttachmentSettings = Record<string, boolean>

interface Section extends SectionAttributes {
  id: Id
  hashid: HashId
  created_at?: string
  updated_at?: string
}

interface SectionAttributes {
  title: string
  color?: string | null
  sort_index: number
}

type LabelledSection = Section & {
  ariaLabelledby?: string
}

interface WallGroupColor {
  id: PostColorId
  title: string
}

interface WallGroupAuthor {
  id: NonNullable<User['hashid']>
  title: string
  avatar?: string
}

interface WallGroupSingleSelect {
  id: NonNullable<WallCustomPostSingleSelectProperty['selection_options'][number]['id']>
  title: string
}

interface WallGroupNone {
  id: WallWishGroupByTypes.None
}

/**
 * Represents a group container that posts can belong to
 */
type WallGroupPost = LabelledSection | WallGroupColor | WallGroupAuthor | WallGroupSingleSelect | WallGroupNone

type WallWishGroupByCustomProp = `custom_prop:${WallCustomPostProperty['id']}`

/**
 * Represents the active grouping strategy/mode for the wall.
 * Determines how posts are organized and which groups are available.
 *
 * Examples:
 * - WallWishGroupByTypes.Section: Group by sections
 * - WallWishGroupByTypes.Color: Group by post color
 * - "custom_prop:<id>": Group by a custom property
 */
type WallWishGroupBy = WallWishGroupByTypes | WallWishGroupByCustomProp

interface DeleteSectionOptions {
  showSnackbar?: boolean
}

interface PostConnection {
  id: Id
  label: string
  from_wish_id: Id
  to_wish_id: Id
}

// Personal  plans
interface Plan {
  id: string
  name: string
  period: 'monthly' | 'annual'
  price: string
  formattedPrice: string
  currency: string
  tier: 'platinum' | 'gold'
  is_legacy: boolean
  url: string
  annual_savings_percentage: number
}

interface AccumulatedNonEmojiReactionData {
  sum: number
  totalReactionsCount: number
  reactionType: Exclude<ReactionType, ReactionType.Emoji>
  userReaction?: Exclude<Reaction, 'value'> & { value: number }
}

interface AccumulatedEmojiReactionData {
  emojis: Record<string, number>
  totalReactionsCount: number
  reactionType: ReactionType.Emoji
  userReaction?: Exclude<Reaction, 'value'> & { value: string }
}

type AccumulatedReactionData = AccumulatedNonEmojiReactionData | AccumulatedEmojiReactionData
type AccumulatedReactions = Record<Id, AccumulatedReactionData | undefined>

// Estimated cancellation refund
interface EstimateCancellationRefundResult {
  amount: string
  currency: string
}

// Library plans
type LibraryPlan = Plan

// Library invoices
type LibraryInvoice = Invoice

// Backpack plans
interface BackpackPlanPrices {
  '10'?: number
  '15'?: number
  '25'?: number
  '35'?: number
  '50'?: number
  '75'?: number
  '100'?: number
  '125'?: number
}

interface BackpackPlanResult {
  prices: BackpackPlanPrices
  currency: string
}

interface GoogleDriveAuthorizationResult {
  linkedEmail: string | null
  accessToken: string | null
}

interface MicrosoftOneDriveAuthorizationResult {
  linkedEmail: string | null
  accessToken: string | null
  accountType: string | undefined
  filepickerBaseUrl: string | undefined
  byPathListUrl: string | undefined
}

// Estimated downgrade to Team Neon refund
interface EstimateTeamNeonDowngradeRefund {
  amount: string
  currency: string
}

// Library Change card URL
interface ChangeCardUrl {
  change_card_url: string
}

// Downgrade to Team Neon result
interface LibraryDowngradeToTeamNeonResult {
  status_link: string
}

// Downgrade to Team Neon result
interface LibrarySwitchResult {
  status_link: string
}

interface LibrarySwitchStatusResult {
  switch_status: {
    is_switched: boolean
    is_refunded?: boolean
    error_message?: string
  }
}

interface BillingInformation {
  name?: string
  address: {
    line1?: string
    line2?: string
    postal_code?: string
    city?: string
    state?: string
    country?: string
  }
  update_url?: string
}

interface BillingFree {
  tier: 'neon'
}

interface BillingPaid {
  plan: PlanDisplay
  period: 'year' | 'month'
  card: Card
  tier: 'silver' | 'gold' | 'platinum'
  payment_channel: 'android' | 'ios' | 'chargebee' | null
  billing_information: BillingInformation
  status: {
    is_paused: boolean
    is_pausing_soon: boolean
    is_plan_pausable: boolean
    is_cancelling_soon: boolean
    is_in_trial: boolean
    is_refundable: boolean
    is_purchased_via_reseller: boolean
  }
  switch_plan_status: {
    is_switching_soon: boolean
    new_plan_id: string
  }
  pause_date: string | null
  resume_date: string | null
  term_ends_date: string | null
  cancel_date: string | null
  trial_end_date: string | null
  is_sponsored?: boolean
}

type Billing = BillingFree | BillingPaid

// Result from /billing/pause, /billing/resume, /billing/cancel_pausing
// which is the result (i.e. fields in billing to update) for the operations
// @see @padlet/arvo for type definition for Billing
interface BillingPauseResult {
  is_plan_pausable: boolean
  is_paused: boolean
  is_pausing_soon: boolean
  pause_date: string
  resume_date: string
  term_ends_date: string
}

// Result from /premium/undo_scheduled_plan_switch or /premium/undo_scheduled_cancellation
interface UndoScheduledChangeResult {
  success?: boolean
  errors?: string[]
}

type UndoLibraryScheduledChangeResult = UndoScheduledChangeResult

interface EstimateSwitchingCostResult {
  is_refund: boolean
  currency_code: string
  new_plan_cost: string
  unused_portion: string
  refund_amount: string
  amount_due: string
  invoice_amount: string
}

type EstimateLibrarySwitchingCostResult = EstimateSwitchingCostResult

interface ScheduledChangesResult {
  data: {
    has_scheduled_changes: boolean
  }
}

interface Quota {
  max_upload_megabytes: number | null
  max_walls: number | null
  max_whiteboard_frames: number | null
  max_video_length_seconds: number | null
}

interface AdvertisedQuotasResult {
  neon: Quota
  silver: Quota
  gold: Quota
  platinum: Quota
}

interface AdvertisedQuotasApiResult {
  data: {
    neon: Quota
    silver: Quota
    gold: Quota
    platinum: Quota
  }
}

interface SwitchPlanResult {
  links: {
    status: string
  }
}

interface SwitchCurrencyResult {
  refund_successful: boolean
  switch_successful: boolean
}

type DockableModalState = 'expanded' | 'docked' | 'minimized'

interface UploadedFile extends File {
  isGoogleDriveFile?: boolean
}

interface PopoverAnchor {
  position?: 'top' | 'side' | 'bottom'
  top: number
  left: number
  width: number
  height?: number
}

type ActiveInputTag = 'section' | 'location' | 'postColor' | 'none' | 'singleSelect'

interface LocationMenuPlaceInfo {
  id: string
  name: string
  longitude?: number
  latitude?: number
}

// TODO: Remove this information from Libraries when we have licenses service and API
interface LibraryUserQuotaInfo {
  isCappedPlan?: boolean
  advertizedStudentQuota: number
  advertizedTeacherQuota: number
  advertizedTeacherAndAdminQuota: number
  maxStudentQuota: number
  maxTeacherQuota: number
  maxTeacherAndAdminQuota: number
  studentQuotaHit: boolean
  studentQuotaUsed: number
  teacherQuotaHit: boolean
  teacherQuotaUsed: number
  adminQuotaUsed: number
  ownerQuotaUsed: number
  maxOwnerQuota: number
  maxAdminQuota?: number
}

interface LibraryFreezeState {
  isLibraryFrozen: boolean
  daysSinceUnpaid: number | null
  daysUntilFreeze: number | null
  showBanner: boolean
  visibleTo: LibraryMembershipRole[]
}

interface Library {
  id: LibraryId
  hashid?: HashId
  ownerId: number
  ownerType: string
  name: string
  slug: string
  dashboardUrl: string
  avatar?: string
  defaultAvatar?: string
  bio?: string
  libraryType?: LibraryType
  privacy?: string
  version?: number
  createdAt?: string
  updatedAt?: string
  joinedAt?: string
  libraryAccess?: LibraryAccess
  rosteringEnabled?: boolean
  rosteringSettings?: RosteringSettings
  skolonLinked?: boolean
  addedMembershipsCount?: number
  nonLeftMembershipsCount?: number
  makersCount?: number
  membershipTier?: string
  membershipDuration?: 'monthly' | 'annual'
  quota: {
    wallsUsed: number
    wallsLimit: number
    quotaHit: boolean
    advertizedMaxUploadMegabytes: number
    advertizedWallsLimit: number
    maxUploadMegabytes: number
  }
  trialDaysLeft?: number
  isTrialBehaviorEnabled?: boolean
  isDowngradedFromPayingTier?: boolean
  isOwnerTeacherOrStaff?: boolean
  isPlanCancelled: boolean
  isTrialExpired?: boolean
  isDeprecatedFreeTier: boolean
  userQuota?: LibraryUserQuotaInfo
  visible?: boolean
  host?: string
  isArchiveEnabled: boolean
  membership?: any[] // eslint-disable-line @typescript-eslint/no-explicit-any
  ltiEnabled?: boolean
  ltiUserGroupSyncEnabled?: boolean
  linkedMicrosoftTenantInfo?: LinkedMicrosoftTenantInfo
  lmsUserGroupSyncConfigEnabled?: boolean
  districtSchoolLmsAccessEnabled?: boolean
  districtSchoolLmsName?: string
  districtSchoolLmsAccessConfigEnabled?: boolean
  libraryCanManageLicenses?: boolean
  linkedGoogleClassroomEmail?: string | null
  linkedGoogleWorkspaceEmail?: string | null
  hasAccessControlSettingsV2?: boolean
  libraryFreezeState?: LibraryFreezeState
  customSchoolLibraryType?: string
}

interface RosteringSettings {
  classlink?: boolean
  clever?: boolean
  googleAppLicensing?: boolean
  googleWorkspace?: boolean
  oneroster?: boolean
}

// Library Integrations
interface GoogleClassroomCourse {
  id: string
  name: string
  section: string
  state: string
  alternateLink: string
}

interface LibraryBillingFree {
  tier: 'neon' | 'trial'
}

interface StairstepPriceTier {
  starting_unit: number
  ending_unit: number
  price: number
  formatted_price: number
  formatted_price_with_currency: string
}

interface LibraryBillingPaid {
  customer?: {
    purchased_via_reseller?: boolean
  }
  plan: PlanDisplay
  users_billed_count: number
  card: Card
  tier: 'gold'
  period: 'year' | 'month'
  payment_channel: 'chargebee'
  billing_information: BillingInformation
  status: {
    is_cancelling_soon: boolean
    is_refundable: boolean
  }
  switch_plan_status: {
    is_switching_soon: boolean
    new_plan_id: string | null
  }
  switch_plan_quantity_status: {
    is_switching_soon: boolean
    new_plan_quantity: number | null
  }
  term_ends_date: string
  price_tiers?: StairstepPriceTier[] | []
}

type LibraryBilling = LibraryBillingFree | LibraryBillingPaid

interface UserLibrarySetting {
  libraryId: string
  avatar: string
  name: string
  namespace: string
  libraryType: LibraryType
  visible: boolean
  disabled: boolean
}

type SchoolLibraryStudentPermission = Record<SchoolStudentPermissionBoolean, boolean> &
  Record<SchoolPermissionString, string>

type SchoolLibraryTeacherPermission = Record<SchoolTeacherPermissionBoolean, boolean> &
  Record<SchoolPermissionString, string>

interface SchoolLibraryPermission {
  settings: {
    student: SchoolLibraryStudentPermission
    teacher: SchoolLibraryTeacherPermission
  }
}

type TeamLibraryMemberPermission = Record<TeamPermissionString, string>

interface TeamLibraryPermission {
  settings: {
    member: TeamLibraryMemberPermission
  }
}

interface CanSetWallPrivacyOption {
  name: WallPrivacyOption | WallPrivacyOptionV2
  canSet: boolean
}

interface CanSetWallPrivacyOptions {
  privacyOptions: CanSetWallPrivacyOption[]
}

type OnboardingTask =
  | 'addNewTeam'
  | 'addMembers'
  | 'customizeTeamInfo'
  | 'makePrivateWall'
  | 'pickPlan'
  | 'connectSchoolSso'
  | 'addAdmins'
  | 'addTeachersAndStudents'
  | 'manageUserPermissions'

interface OnboardingData {
  completedAt: string | null
  id: OnboardingFlowId
  libraryId: LibraryId
  tasks: Record<OnboardingTask, boolean | string>
}

interface OnboardingTaskData {
  key: OnboardingTask
  icon: string
  taskStringOne: string
  taskStringTwo: string
  taskButtonText: string
  taskButtonLabel: string
  isTaskDone: boolean
  taskDoneAt: string | null
  order: number
  hideTaskForAdmins?: boolean
}

interface LibraryMembership {
  id: LibraryMembershipId
  libraryId: LibraryId
  userId: UserId
  role: LibraryMembershipRole
  status: 'added' | 'deactivated' | 'suspended' | 'left' | 'invited'
  user: Partial<User>
  updatedAt: string
  createdAt: string
  lastActiveAt?: string | null
}

interface LibraryUserEmailDomain {
  domainName: string
  role: LibraryMembershipRole
  pending: boolean
}

export enum LibraryOauthProvider {
  GOOGLE = 'google',
  MICROSOFT = 'microsoft',
  APPLE = 'apple',
  CLEVER = 'clever',
  CLASSLINK = 'classlink',
  PASSWORD = 'password',
}

interface LibraryOauthMethod {
  name: LibraryOauthProvider
  enabled: boolean
}

interface LibraryOauthSettings {
  oauth: LibraryOauthMethod[]
  cleverDistrictId?: string | null
  classlinkTenantId?: string | null
}

interface LibraryAccess {
  canViewLibraryInfo: boolean
  canUpdateLibraryInfo: boolean
  canSetupLti: boolean
  canSetupGoogleClassroom?: boolean | null
  canSetupGoogleWorkspace?: boolean | null
  canSetupMicrosoftTeams?: boolean | null
  canUpdatePermissions: boolean
  canViewLibraryMemberships: boolean
  canViewLibraryAnalytics: boolean
  canManageMembers: boolean
  canManageBilling: boolean
  canTrashLibrary: boolean
  canUseLibraryAsOwner: boolean
  canUseLibraryAsAdmin: boolean
  canUseLibraryAsTeacher: boolean
  canViewLibrarySecurity: boolean
  canViewLibraryConnections: boolean
  canInviteSchoolAdmins: boolean
  canInviteSchoolTeachers: boolean
  canInviteSchoolStudents: boolean
  canInviteMembers: boolean
  canViewGallery: boolean
  canManageContentSafetySettings: boolean
  canTransferWalls: boolean
  canUseAiRecipes: boolean
}

interface LibraryInviteLinks {
  id: Id
  linkForAdmin?: string
  linkForMaker?: string
  linkForContributor?: string
  linkForTeacher?: string
  linkForStudent?: string
  codeForAdminEnabled: boolean
  codeForMakerEnabled?: boolean
  codeForContributorEnabled?: boolean
  codeForTeacherEnabled?: boolean
  codeForStudentEnabled?: boolean
}

interface LinkedMicrosoftTenantInfo {
  microsoftTenantId?: string | null
  adminConsentGranted: boolean
}

// Json Api
// eslint-disable-next-line @typescript-eslint/no-explicit-any
interface JsonApiResponse<T, I = any> {
  data?: JsonApiData<T> | Array<JsonApiData<T>>
  errors?: JsonApiError[]
  links?: JsonApiLinks
  meta?: JsonApiMeta
  included?: JsonApiData<I> | Array<JsonApiData<I>>
}

interface JsonApiData<T> {
  id: string
  type: string
  attributes: T
}

interface JsonApiError {
  status: string
  title: string
  detail: string
  code: string
  meta: object
}

interface JsonApiLinks {
  next: string
}

interface JsonApiMeta {
  page: {
    hasNextPage: boolean
    nextPage: number | null
  }
}

// #region Campus Discussions
// A discussion thread within a class, camelCased by the Fast serializer.
interface CampusDiscussion {
  id: CampusDiscussionId
  classId: number
  topic: string
  description?: string | null
  attachmentUrl?: string | null
  messagesCount: number
  newMessagesCount: number
  hasNewMessages: boolean
  unapprovedMessagesCount?: number
  authorName?: string | null
  authorAvatarUrl?: string | null
  pinned: boolean
  createdAt: string
  updatedAt?: string
  canUpdateDiscussion?: boolean
  canPinDiscussion?: boolean
}
// #endregion

// #region Campus Discussion Lists and Messages
interface DiscussionsListMeta extends JsonApiMeta {
  canCreateDiscussion?: boolean
}

interface DiscussionsListPage {
  items: CampusDiscussion[]
  hasMore: boolean
  canCreateDiscussion?: boolean
}

// TODO: Align message id to number once backend persists to final table
type DiscussionMessageId = Id | string
interface DiscussionMessage {
  id: DiscussionMessageId
  discussionId: CampusDiscussionId
  creatorId: Id
  content: string | null
  attachmentUrl?: string | null
  moderationStatus?: string | null
  createdAt: string
  updatedAt?: string
}

interface DiscussionMessagesPage {
  items: DiscussionMessage[]
  nextCursorId?: DiscussionMessageId | null
  prevCursorId?: DiscussionMessageId | null
}

interface DiscussionMessagesMeta extends JsonApiMeta {
  page: JsonApiMeta['page'] & { nextCursorId?: DiscussionMessageId | null; prevCursorId?: DiscussionMessageId | null }
}
// #endregion

interface WallCreationFromTemplateResult {
  url: string
}

interface WallNameAvailability {
  available: boolean
}

// Wall Access
interface UserWallCollaborator {
  right: WallAccessRight
  role?: WallAccessRole
  type: 'user'
  id: UserId
  avatar: string
  email: string
  name: string
  username: string
  shortName?: string
  isRegistered: boolean
  isLibraryMember?: boolean
  status: WallCollaboratorStatus
}

interface UserWallCollaboratorInviteByUserId {
  wallId: Id
  role?: WallAccessRole
  email?: never
  userId: Id
}

interface UserWallCollaboratorInviteByEmail {
  wallId: Id
  role?: WallAccessRole
  email: string
  userId?: never
}

type UserWallCollaboratorInvite = UserWallCollaboratorInviteByUserId | UserWallCollaboratorInviteByEmail

interface UserGroupWallCollaborator {
  id: Id
  avatar: string
  name: string
  right?: WallAccessRight
  role?: WallAccessRole
  status?: WallCollaboratorStatus
  type: 'userGroup'
}

type MarkType = 'score'
interface WallGradingContextFile {
  name: string
  url: string
}
interface WallGradingSettings {
  markType?: MarkType
  maxScore?: number
  gradeCalculationMethod?: WallGradeCalculationMethods
  lmsPassbackEnabled?: boolean
  showLmsPassback?: boolean
  googleClassroomPassbackEnabled?: boolean
  showGoogleClassroomPassback?: boolean
  lmsLastSynced?: string
  lmsSyncState?: LmsSyncState
  lmsSyncErrorMessage?: string
  aiCustomization?: string
  contextFiles?: WallGradingContextFile[]
}

interface LmsPassbackResponse {
  userSyncState: SyncStateUserIds
  lmsLastSynced: string
  lmsSyncState: LmsSyncState
  lmsSyncErrorMessage: string
  async?: boolean
}

interface SyncStateUserIds {
  successfulUserIds: Id[]
  failedUserIds: Id[]
  skippedUserIds: Id[]
}

interface WallAccessSettings {
  wallId?: WallId
  privacyPolicyId?: PrivacyPolicyId
  privacyPolicyVersion?: number
  privacyType: WallAccessPrivacyType
  right: WallAccessRight
  role?: WallAccessRole
  publicRole?: WallAccessRole
  libraryRole?: WallAccessRole
  tenantRole?: WallAccessRole
  visitorRole?: WallAccessRole
  studentRole?: WallAccessRole
  teacherRole?: WallAccessRole
  requireLogin?: boolean
  requirePassword?: boolean
  isPublished?: boolean
  isListed?: boolean
  password: string | null
  collaborators: UserWallCollaborator[]
}

type WallAccessSettingsToggles = Pick<
  WallAccessSettings,
  'requireLogin' | 'requirePassword' | 'isPublished' | 'isListed'
>

interface WallOnboardingPanel {
  wallId?: WallId
}

interface WallGalleryOnboardingData {
  id: string
  templateName: string
  templateDescription: string
  templatePadletUrl: string
  status: 'Internal' // If there are more potential statuses, you might want to expand this into a union type
  shouldCopyWallpaper: boolean
  postTypes: string // 'none' is the only value here, but if there are more possible values, this should be a union type
  previewPadletUrl: string
  samplePosts: number[][]
  templatePublicKey: string
  category: string[] // Assuming that this is an array of strings
  thumbnailUrl: string
  templateWallId: null // Assuming this is always null based on the provided object
  steps: Array<{
    note: string
    action: string | null // We should make this enum of possible values
  }>
}

interface WallLayout {
  name: string
  value: string
  image_path: {
    desktop: string
    mobile: string
  }
  template_url: string
  long_description: string
}

interface WallLayoutPreviewImage {
  name?: string
  path: string
  originalWidth: string
  originalHeight: string
}

interface WallLayoutAttributes {
  name: string
  value: string
  groupBy: 'section' | null
  icon: {
    path: string
    originalWidth: string
    originalHeight: string
  }
  preview: WallLayoutPreviewImage
  previewMobile: WallLayoutPreviewImage
  thumbnail: {
    path: string
    originalWidth: string
    originalHeight: string
  } | null
}

interface WallSectionBreakoutLink {
  wall_section_id: number
  link: string
}

// when a user visits a breakout link, this is set in starting state.
// see to_h methods of section_breakout.rb and color_breakout.rb etc
export type BreakoutLinkConfig =
  | SectionBreakoutLinkConfig
  | ColorBreakoutLinkConfig
  | AuthorBreakoutLinkConfig
  | SingleSelectBreakoutLinkConfig

export interface SectionBreakoutLinkConfig {
  id: string
  wall_id: number
  wall_section_hashid: string
  wall_section_id: number
  sharelink_hashid: string
  link: string
  type: 'section_breakout'
}

export interface ColorBreakoutLinkConfig {
  id: string
  wall_id: number
  color: PostColorId
  link: string
  type: 'color_breakout'
}

export interface AuthorBreakoutLinkConfig {
  id: string
  wall_id: number
  link: string
  type: 'author_breakout'
}

export interface SingleSelectBreakoutLinkConfig {
  id: string
  wall_id: number
  custom_prop_id: string
  option_id: string
  link: string
  type: 'single_select_breakout'
}

interface WallLinks {
  /** URL to a padlet */
  show: string
  /** Like show, but with variant and app version */
  app: string
  /** URL to a padlet. Usually equal to `show` */
  doodle: string
  /** Permalink to a padlet. */
  permanent: string
  /** RSS feed for a particular padlet. */
  feed?: string
  /** Special full embed view of a padlet. */
  embed: string
  /** Partial embed view of a padlet. Good for smaller screens. */
  preview_embed: string
  /** Exported screenshot of a padlet. */
  snap: string
  /** Cover image of a padlet. Can be changed by the creator/administrator of the padlet. */
  peek: string
  /** Printable view of a padlet. */
  print: string
  /** Downloadable Excel spreadsheet version of the padlet. */
  spreadsheet: string
  list: string
  /** Downloadable Markdown version of the padlet. */
  markdown: string
  /** Exported screenshot of a padlet. */
  screenshot: string
  /** Downloadable PDF version of the padlet. */
  document: string
  /** Deeplink to a padlet that can be opened via our mobile app. */
  deeplink: string
  /** Downloadable QR code that when scanned would bring the user to the padlet. */
  qr_code: string
  /**
   * A viewable PDF document containing a QR code.
   * When scanned, this QR code directs the user to the specific padlet.
   */
  qr_code_pdf: string
  bot_viewable: string
  /** Slideshow URLs */
  slideshow_qr_code: string
  slideshow_document: string
}

interface WallSubmissionRequestProps {
  requires_login: boolean
  redirect_location: string
  confirmation_text?: string
}

interface WallSubmissionRequestLink extends WallSubmissionRequestProps {
  link: string
}

interface WallRemakeLinkProps {
  copyPosts: string
  useOriginalPostsAuthorship: boolean
  useOriginalWallpaper: boolean
  addAdminsAsCollaborator: boolean
  limitToOneRemakePerUser: boolean
}

interface WallWhiteboardRemakeLinkProps {
  copyObjects: string
  useOriginalWallpaper: boolean
  addAdminsAsCollaborator: boolean
  limitToOneRemakePerUser: boolean
}

interface WallRemakeLink extends WallRemakeLinkProps {
  link: string
}

interface WallWhiteboardRemakeLink extends WallWhiteboardRemakeLinkProps {
  link: string
}

interface SharedCollection {
  type: 'user' | 'library'
  avatar: string
  name: string
  id: Id
}

// Notifications

interface NotificationChannelInformation {
  notify: boolean
  mandatory: boolean
  available: boolean
}

type NotificationChannelList = Record<NotificationChannelType, NotificationChannelInformation>

interface NotificationSettingApiResponse {
  notification_type: NotificationType
  channels: NotificationChannelList
}

// User follows

interface UserFollowApiResponse {
  followerId: UserId
  user: Partial<User>
}

// Wall follows

interface WallFollowApiResponse {
  userId: UserId
  wall: Partial<Wall>
  follows: boolean
}

interface WallFollowInformation {
  current_user_follows_wall: boolean
  wall_follow_id: Id | null
  wall_follows_count: number
}

// Wall Read Token

interface WallReadTokenResponse {
  token: string
}

// Wall RSS Feed

interface WallRssFeedUrlResponse {
  rss_feed_url: string
  podcast_feed_url: string
  georss_feed_url: string
}

// Magic Walls

interface MagicWallCreationResult {
  url: string
}

interface MagicWallCreationStatusResult {
  status: string
  wall_url: string
  wall_id: number
}

interface MagicTemplate {
  name: string
  key: string
  icon: string
  imageSrc: string
  originalImageWidth: number
  originalImageHeight: number
  isNew?: boolean
  shortName?: string
}

interface BaseMagicTemplateInputConfig {
  label: string
  refKey: string
  optional?: boolean
  toCache?: boolean
  defaultValue?: string
}

interface MagicTemplateTextConfig extends BaseMagicTemplateInputConfig {
  type: 'text'
  placeholder: string
}

interface MagicTemplateNumberRangeConfig extends BaseMagicTemplateInputConfig {
  type: 'number-range'
  low: number
  high: number
}

interface MagicTemplateMultilineTextConfig extends BaseMagicTemplateInputConfig {
  type: 'multiline-text'
  placeholder: string
  hasExampleButton?: boolean
  exampleFunction?: () => string
}

interface MagicTemplateGradeConfig extends BaseMagicTemplateInputConfig {
  type: 'grade'
}

interface MagicTemplateDifficultyConfig extends BaseMagicTemplateInputConfig {
  type: 'difficulty'
}

// Union of all types
type MagicTemplateInputConfig =
  | MagicTemplateTextConfig
  | MagicTemplateMultilineTextConfig
  | MagicTemplateGradeConfig
  | MagicTemplateDifficultyConfig
  | MagicTemplateNumberRangeConfig

interface MagicTemplateViewConfig {
  description: string
  inputConfig: MagicTemplateInputConfig[]
}

// Guided Templates
interface DiscussionQuestion {
  question: string
  summary: string
}

interface QuestionAndResponseDiscussionQuestion extends DiscussionQuestion {
  discussion_style: DiscussionStyle.QuestionAndResponse
}

interface DebateDiscussionQuestion extends DiscussionQuestion {
  discussion_style: DiscussionStyle.Debate
  section_one: string
  section_two: string
  section: string
}

interface QuestionAndResponseResponse extends DiscussionQuestion {
  answer: string
}

interface DebateResponse {
  answer: string
  section: string
}

type GuidedDiscussionQuestion = QuestionAndResponseDiscussionQuestion | DebateDiscussionQuestion
type GuidedDiscussionResponse = QuestionAndResponseResponse | DebateResponse

interface GuidedDiscussionQuestionsResponse {
  questions: GuidedDiscussionQuestion[]
}

interface GuidedDiscussionResponsesResponse {
  responses: GuidedDiscussionResponse[]
}
interface ExitTicketConfigurationResult {
  url: string
}

interface ExitTicketConfigurationStatusResult {
  status: string
  wall_url: string
  data: {
    activity_message: string
  }
}

interface GuidedTemplateGrade {
  level: string
  description: string
}

// AI Talk
interface AiTalkResponse {
  url: string
}

interface Voice {
  languageCode: string
  languageName: string
  name: string
}

interface AiTalkLanguageResponse {
  languageCode: string
}

// Google Drive Uploads

interface BeethovenUploadError {
  status: string
  title: string
  detail: string
  code: string
}
interface GoogleDriveUploadResponse {
  url: string
  errors?: BeethovenUploadError[]
}

interface MicrosoftOneDriveUploadResponse {
  url: string
  errors?: BeethovenUploadError[]
}

// TODO: Replace with Vue-definied one after moving to Vue3.
type VueCssClass = string | CSSProperties | VueCssClass[]

// Feed

interface PostFeedItemData {
  post: Post
  author: User
}

// Mentions

type UserMentionSuggestionAttributes = Required<
  Pick<UserWithHashId, 'id' | 'hashid' | 'display_name' | 'username' | 'avatar'>
> & { has_access?: boolean }
type UserMentionSuggestion = Pick<JsonAPIResource<UserMentionSuggestionAttributes>, 'type' | 'attributes'>
interface Command {
  name: string
  description: string
  icon: string
  level: CommandLevel
  action: () => void
  filter?: () => void
}

// Dashboard settings

interface UserLibrarySettingsApiResponse {
  defaultLibrary: UserLibrarySetting
  librarySettings: UserLibrarySetting[]
}

// Verification Code
type VerificationCodePurpose = 'change_email_address' | 'signup' | 'login'

interface EmailAddressVerificationResult {
  emailAddress: string
  status: string
  loginAsUserUrl?: string
}

// Folders

interface FolderApiResponse {
  id: Id
  name: string
  wallIds: WallId[]
}

// User groups

interface UserGroupFolderApiResponse {
  id: UserGroupId
  name: string
  icon?: string
  grade?: string
  subject?: string
  allowStudentAddPadlet?: boolean
  allowStudentAddDiscussion?: boolean
  currentUserClassRole?: ClassRole.Teacher | ClassRole.Student
  wallIds: WallId[]
}

interface ContentModerationCategoryDetails {
  name: ContentModerationCategory
  enabled: boolean
  modifiable: boolean
}
interface ContentSafetySettings {
  contentModerationMode: ContentModerationMode
  moderationCategories: ContentModerationCategoryDetails[]
}

export enum AttachmentTypeId {
  Padlet = 'padlet',
  Upload = 'upload',
  Link = 'link',
  Camera = 'camera',
  VideoRecorder = 'videoRecorder',
  ArLensesEnabled = 'arLensesEnabled',
  AudioRecorder = 'audioRecorder',
  ScreenRecorder = 'screenRecorder',
  Draw = 'draw',
  Poll = 'poll',
  GoogleDrive = 'googleDrive',
  ICantDraw = 'iCantDraw',
  TalkForMe = 'talkForMe',
  PhotoAlbum = 'photoAlbum',
  ImageSearch = 'imageSearch',
  Stickers = 'stickers',
  Gif = 'gif',
  Youtube = 'youtube',
  Spotify = 'spotify',
  WebSearch = 'webSearch',
  Location = 'location',
  SoundAndVisualEffects = 'soundAndVisualEffects',
}

export enum AttachmentTypeMode {
  DefaultOn = 'default_on',
  DefaultOff = 'default_off',
  Disabled = 'disabled',
}

// These are the library attachment settings
interface AttachmentTypeSettings {
  modes: Record<AttachmentTypeId, AttachmentTypeMode>
}

// These are the permissions which the library attachment settings map to.
// Note that the settings and permissions are not a 1-1 mapping.
// For example, AttachmentTypeId.Upload maps to AttachmentTypeSettingsPermission.File
// and AttachmentTypeSettingsPermission.MediaLibrary.
export enum AttachmentTypeSettingsPermission {
  File = 'file', // Maps from AttachmentTypeId.Upload
  Link = 'link',
  WebSearch = 'webSearch',
  PhotoBooth = 'photoBooth', // Maps from AttachmentTypeId.Camera
  VideoRecorder = 'videoRecorder',
  ArLensesEnabled = 'arLensesEnabled',
  AudioRecorder = 'audioRecorder',
  Drawing = 'drawing', // Maps from AttachmentTypeId.Draw
  Padlet = 'padlet',
  LocationSearch = 'locationSearch', // Maps from AttachmentTypeId.Location
  ScreenRecorder = 'screenRecorder',
  AiImageSearch = 'aiImageSearch', // Maps from AttachmentTypeId.ICantDraw
  ImageSearch = 'imageSearch',
  StockImageSearch = 'stockImageSearch', // Maps from AttachmentTypeId.ImageSearch
  Stickers = 'stickers',
  VideoSearch = 'videoSearch', // Maps from AttachmentTypeId.Youtube
  AudioSearch = 'audioSearch', // Maps from AttachmentTypeId.Spotify
  GifSearch = 'gifSearch', // Maps from AttachmentTypeId.Gif
  Poll = 'poll',
  GoogleDrive = 'googleDrive',
  MicrosoftOneDrive = 'microsoftOneDrivePicker',
  AiTalk = 'aiTalk', // Maps from AttachmentTypeId.TalkForMe
  MediaLibrary = 'mediaLibrary', // Maps from AttachmentTypeId.Upload, only available on mobile
  PhotoAlbum = 'photoAlbum', // Maps from AttachmentTypeId.PhotoAlbum
  SoundAndVisualEffects = 'soundAndVisualEffects',
}

type AttachmentTypeSettingsPermissions = Record<AttachmentTypeSettingsPermission, boolean>

interface ContentModerationCategoryRequest {
  name: ContentModerationCategory
  enabled: boolean
}
interface ContentSafetySettingsUpdateApiRequest {
  contentModerationMode?: ContentModerationMode
  moderationCategories?: ContentModerationCategoryRequest[]
}

// Grading, follows grade.rb
interface Grade {
  gradableType: Gradable
  gradableId: Id
  gradedUserId: UserId
  graderUserId?: UserId
  category?: GradeCategory
  value?: string
}

interface CalculatedWallGrade {
  authorId: UserId
  grades: number[]
  calculatedGrade?: number
}

// Google App Licensing
interface GoogleAppLicensingSettingsResponse {
  enabled: boolean
  orderId?: string
  redeemUri?: string
}

interface GoogleAppLicensingCreateResponse {
  orderId?: string
  redeemUri?: string
}
interface GoogleAppLicensingValidateResponse {
  invalidLicense: boolean
  nextUrl?: string
}

// Library Invites
interface LibraryInvitedUser {
  email?: string
  name?: string
  password?: string
  role: string
}

interface LibraryPendingInvitation {
  email: string
  role: LibraryMembershipRole
  invited_at: string
  library_id: LibraryId
}

interface LibraryInvitedUserValidationResult extends LibraryInvitedUser {
  valid: boolean
  reason?: string
}

// Personal To Library Transfer Walls
interface PersonalToLibraryTransferWallsResponse {
  completed: boolean
}

// Activate Bulk Pro
interface InvoiceDetails {
  email: string
  invoiceId: string
  invoiceLink: string
  invoiceType: string
  name: string
  subscriptionId: string
  subscriptionUrl: string
  userId: number
}

interface InvoiceCheckResponse {
  billingSubscriptionId: string
  billingSubscriptionUrl: string
  invoice: Record<string, any> // eslint-disable-line @typescript-eslint/no-explicit-any
  invoiceDetails: InvoiceDetails
  licensesLeft: number
  payment: Record<string, any> // eslint-disable-line @typescript-eslint/no-explicit-any
  purchasingUser: User
  usersList: User[]
  warningsList: string[]
  paidDomains: string[]
  input_error?: string
}

interface AddLicenseResponse {
  addedCount: number
  usersList: User[]
  successMessage?: string
  warningUsers?: Record<string, string>
  failedUsers?: Record<string, any> // eslint-disable-line @typescript-eslint/no-explicit-any
}

interface FailedToCancelLicenseError {
  id: string
  email: string
  error: string
}

interface RemoveLicenseResponse {
  cancelled: UserId[]
  failedToCancel: FailedToCancelLicenseError[]
}

interface PaymentStatusResponse {
  unpaidInvoices: number
  changePaymentMethodUrl: string
}

interface AccountMenuItem {
  tenantId: TenantId
  key: AccountKey
  displayName: string
  logo: string
  fallbackLogo?: string
  tenantDomainName?: string
  isNative: boolean
  isLibrary: boolean
  isSameDomain: boolean
  libraryId: LibraryId | null
  url: string
  wallsUsed: number
  wallsLimit: number
  remainingWallsQuota: number
  canMakeWall: boolean
  canArchive: boolean
  username?: string
  slug?: string
  canViewGallery?: boolean
  membershipTier?: string
}

// SAML

interface SamlConnectionApiResponse {
  id: Id
  allowUpgradeToTeacherRole: boolean
  devMode: boolean
  emailAttribute: string
  idpMetadataUrl: string
  connectionName: string
  nameAttribute: string
  roleAttribute: string
  spEntityId: string
}

// Powwow

interface ArvoConfig {
  cdnHost: string
  jsonApiHost: string
  token: {
    oauthToken: string
  }
}

interface CreateSessionUserApiResponse {
  arvoConfig: ArvoConfig
  user: User
}

interface ModerateUserNameApiResponse {
  userNameHasViolation: null | boolean
}

interface LinkSafetyCheckApiResponse {
  isSafe: string
}

// #region Whiteboard
interface WhiteboardCollaborationSettings {
  collaboration_cursors: ShownCursorType
}

type WhiteboardToolSettings = Record<string, boolean>

interface WhiteboardSoundEffect {
  id: string
  name: string
  category: string
  thumbnail_url: string
  audio_url: string
  published: boolean
  in_review: boolean
  translated_name: string
  translated_category: string
  translated_search_keywords: string[]
}

interface WhiteboardCardTemplate {
  id: string
  thumbnail_url: string
  preview_url: string
  page_snapshot: {
    page: TLPage
    records: TLRecord[]
  }
  name: string
  translated_name: string
  translated_category: string
  translated_search_keywords: string[]
}
// #endregion

type CssClass = string | string[] | Record<string, boolean> | CssClass[]

// #region Wall analytics
interface WallAnalytics {
  visitors: number
  pageviews: number
  engagementTimeMin?: number
}
// #endregion

interface WhiteboardCardBackground {
  id: string
  name: string
  url: string
  thumbnailUrl: string
  category: string
  width: number
  height: number
}

interface WhiteboardCardBackgroundsApiResponse {
  backgrounds: WhiteboardCardBackground[]
}

interface WhiteboardDefaultFrameBackground {
  backgroundType: string
  backgroundUrl: string
}

type WhiteboardDefaultFrameBackgroundApiResponse = WhiteboardDefaultFrameBackground

// Wall translations
interface TranslatedAttachmentProps extends AttachmentProps {
  type?: 'poll'
  pollId?: number
  question?: string
  pollChoices?: Array<{
    id: string
    choiceText: string
  }>
}
interface TranslatedWish {
  wishId: string
  sectionId: string
  subject: string
  body: string
  attachmentCaption: string
  customProperties: PostCustomProperties
  attachmentProps?: TranslatedAttachmentProps
  detectedLanguage?: string
}

interface TranslatedSection {
  sectionId: string
  sectionTitle: string
}

interface TranslatedComment {
  commentId: string
  wishId: string
  htmlBody: string
  detectedLanguage?: string
}

interface TranslatedCustomPostProperty {
  id: string
  name: string
}

interface TranslatedSelectionOption {
  propertyId: string
  id: string
  name: string
}

interface TranslatedWall {
  title: string | null
  description: string | null
  sections: TranslatedSection[]
  wishes: TranslatedWish[]
  comments: TranslatedComment[]
  customPostProperties: TranslatedCustomPostProperty[] | null
  selectionOptions: TranslatedSelectionOption[] | null
}

interface TranslatedEntity {
  id: string
  entity_type: string
  translation: Partial<TranslatedWall> | TranslatedSection | TranslatedWish | TranslatedComment
}

interface WallDetectedLanguage {
  language: string
}

interface SnackbarLoadingState {
  progress: number
  status: SnackbarLoadingStatus
}

interface SponsoredUser {
  id: number
  user?: Partial<User>
  status: SponsoredUserStatus
  createdAt: string
  expiresAt: string
  updatedAt: string
  sponsoredEmail?: string
}

interface CheckEmailResponse {
  email: string
  valid: boolean
  reason: string
}

interface StripeAddress {
  line1: string
  line2: string | null
  city: string
  state: string
  postal_code: string
  country: string
}

interface StripeInvoiceDetails {
  accountName: string
  productName: string
  totalAmount: number
  currency: string
  taxes: Array<{ amount: number; taxable_amount: number }>
  lineItem: { description: string; quantity: number }
  formattedTaxAmountPrice: string
  formattedTotalAmountPrice: string
  formattedTotalAmountPriceWithoutCurrency: string
  formattedTaxAmountPriceWithoutCurrency: string
  formattedSubtotalPriceWithoutCurrency: string
}

interface ClassInformation {
  value: string
  updated_at: string
}

interface GoogleClassroomStoredAuthPayload {
  subject: string
  token: string
  storedAt: number
}

interface AiMakeSuggestion {
  id: string
  name: string
  short_description: string
  long_description: string
  relevancy: number
  product_type: number
  created_at: string
  padlet_config: {
    wall_type: string
    whiteboard_format?: string
    subject: string
    grades: string[]
    lesson_objectives: string
    time_period?: string
    additional_details?: string
  }
}

interface AiMakeSuggestionsResponse {
  data: AiMakeSuggestion[]
}

export enum BillingProvider {
  Chargebee = 'chargebee',
  Stripe = 'stripe',
}

interface BeltProgressData {
  certificateToken: string
  certificateQrCode: string
  certificateUrl: string
  signatureName?: string
  signatureRole?: string
  signatureStamp?: string
  lastUpdatedAt: string
  metricsForCollaboration: {
    postsFromOtherUsersCount: number
    remakesFromOtherUsersCount: number
    sandboxEngagementTime: number
    commentsFromOtherUsersCount: number
  }
  metricsForCreativity: {
    padletsCount: number
    postsCount: number
  }
  pointsForCollaboration: {
    postsFromOtherUsersCount: number
    commentsFromOtherUsersCount: number
    sandboxEngagementTime: number
    remakesFromOtherUsersCount: number
  }
  pointsForCreativity: {
    padletsCount: number
    postsCount: number
  }
  totalPoints: number
  totalPointsForCollaboration: number
  totalPointsForCreativity: number
  currentBelt: string
}
interface BeltProgressHistoryItem {
  belt: string
  achievedAt: Date
}
interface BeltPointsThreshold {
  minCreativity: number
  minCollaboration: number
  color: string
}

interface CollaborationMultipliers {
  postsFromOtherUsersCount: number
  remakesFromOtherUsersCount: number
  sandboxEngagementTime: number
  commentsFromOtherUsersCount: number
}
interface CreativityMultipliers {
  padletsCount: number
  postsCount: number
  commentsCount: number
}
interface UserAchievements {
  progress: BeltProgressData
  beltProgressHistory: BeltProgressHistoryItem[]
  beltPointsThresholds: BeltPointsThreshold[]
  collaborationMultipliers: CollaborationMultipliers
  creativityMultipliers: CreativityMultipliers
}

interface UserAchievementsDownloadCertificate {
  zipUrl: string
  zipFilename: string
}
interface UserAchievementsDownloadBadge {
  pngUrl: string
  pngFilename: string
}

interface UserAchievementsDownloadCertificateStatus {
  status: string
  data?: UserAchievementsDownloadCertificate
}

interface UserAchievementsDownloadBadgeStatus {
  status: string
  data?: UserAchievementsDownloadBadge
}

interface UserAchievementsAnalyticsWallContributions {
  wallContributions: {
    wallId: number
    wall: WallCamelCase
    creativityPoints: {
      total: number
    }
    collaborationPoints: {
      total: number
    }
    totalPoints: number
  }[]
  pagination: {
    currentPage: number
    perPage: number
    totalCount: number
    totalPages: number
    hasMore: boolean
  }
  summary: {
    totalCreativityPoints: number
    totalCollaborationPoints: number
    totalWalls: number
  }
}

export interface SelfServeTrialExperimentEnrollmentResponse {
  data: {
    type: 'trial_eligibility'
    attributes: {
      eligible: boolean
      trial_duration_days: number
    }
  }
}

export type {
  AccountKey,
  AccountMenuItem,
  AccountType,
  AccumulatedEmojiReactionData,
  AccumulatedNonEmojiReactionData,
  // Reactions
  AccumulatedReactionData,
  AccumulatedReactions,
  ActiveInputTag,
  AddLicenseResponse,
  AdvertisedQuotasApiResult,
  AdvertisedQuotasResult,
  // Ai suggestions on make page
  AiMakeSuggestion,
  AiMakeSuggestionsResponse,
  AiTalkLanguageResponse,
  AiTalkResponse,
  AttachmentProps,
  AttachmentTypeSettings,
  AttachmentTypeSettingsPermissions,
  BackpackPlanPrices,
  BackpackPlanResult,
  // Google Drive Upload
  BeethovenUploadError,
  BeltPointsThreshold,
  BeltProgressHistoryItem,
  Billing,
  BillingFree,
  BillingInformation,
  BillingPaid,
  BillingPauseResult,
  CalculatedWallGrade,
  CampusDiscussion,
  CampusDiscussionId,
  CanSetWallPrivacyOption,
  CanSetWallPrivacyOptions,
  ChangeCardUrl,
  CheckEmailResponse,
  Cid,
  // Class information
  ClassInformation,
  // Comments
  Command,
  Comment,
  CommentAttachmentSettings,
  CommentId,
  ContentModerationCategoryDetails,
  ContentSafetySettings,
  ContentSafetySettingsUpdateApiRequest,
  CreateSessionUserApiResponse,
  // CssClass
  CssClass,
  DateDisplayAs,
  DateDisplayFormat,
  DebateDiscussionQuestion,
  DebateResponse,
  DeepPartial,
  DeleteSectionOptions,
  DeviceDetails,
  DiscussionMessage,
  DiscussionMessageId,
  DiscussionMessagesMeta,
  DiscussionMessagesPage,
  DiscussionsListMeta,
  DiscussionsListPage,
  DockableModalState,
  DomainValidation,
  DraftPost,
  EmailAddressVerificationResult,
  EmojiPreset,
  EstimateCancellationRefundResult,
  EstimateLibrarySwitchingCostResult,
  EstimateSwitchingCostResult,
  // Libraries
  EstimateTeamNeonDowngradeRefund,
  ExitTicketConfigurationResult,
  ExitTicketConfigurationStatusResult,
  FailedToCancelLicenseError,
  FileAttributes,
  FilterGroup,
  Folder,
  FolderApiResponse,
  FolderId,
  FreezeBannerState,
  GoogleAppLicensingCreateResponse,
  GoogleAppLicensingSettingsResponse,
  GoogleAppLicensingValidateResponse,
  GoogleClassroomCourse,
  GoogleClassroomStoredAuthPayload,
  GoogleDriveAuthorizationResult,
  GoogleDriveUploadResponse,
  Grade,
  GuidedDiscussionQuestion,
  GuidedDiscussionQuestionsResponse,
  GuidedDiscussionResponse,
  GuidedDiscussionResponsesResponse,
  GuidedTemplateGrade,
  HashId,
  Id,
  InvoiceCheckResponse,
  InvoiceDetails,
  // Json Api
  JsonApiData,
  JsonApiError,
  JsonApiMeta,
  JsonApiResponse,
  LabelledSection,
  Library,
  LibraryAccess,
  LibraryAnalytics,
  LibraryAttributeCheck,
  LibraryBilling,
  LibraryBillingPaid,
  LibraryDowngradeToTeamNeonResult,
  LibraryId,
  LibraryInvitedUser,
  LibraryInvitedUserValidationResult,
  LibraryInviteLinks,
  LibraryInvoice,
  LibraryMembership,
  LibraryMembershipId,
  LibraryOauthMethod,
  LibraryOauthSettings,
  LibraryPendingInvitation,
  LibraryPlan,
  LibrarySwitchResult,
  LibrarySwitchStatusResult,
  LibraryUserEmailDomain,
  LibraryUserQuotaInfo,
  // Microsoft Teams
  LinkedMicrosoftTenantInfo,
  LinkSafetyCheckApiResponse,
  LmsPassbackResponse,
  LocationMenuPlaceInfo,
  // Magic Wall
  MagicTemplate,
  MagicTemplateInputConfig,
  MagicTemplateTextConfig,
  MagicTemplateViewConfig,
  MagicWallCreationResult,
  MagicWallCreationStatusResult,
  MapPostAttributes,
  MicrosoftOneDriveAuthorizationResult,
  MicrosoftOneDriveUploadResponse,
  ModerateUserNameApiResponse,
  // Notifications
  NotificationChannelInformation,
  NotificationChannelList,
  NotificationSettingApiResponse,
  OnboardingData,
  OnboardingTask,
  OnboardingTaskData,
  OnboardingWallViz,
  PaymentStatusResponse,
  PersonalToLibraryTransferWallsResponse,
  PinDropSuggestion,
  Plan,
  Point,
  PopoverAnchor,
  Post,
  PostAttributes,
  PostColor,
  PostColorId,
  PostConnection,
  PostCustomProperties,
  PostCustomPropertyValue,
  // Feed
  PostFeedItemData,
  PostPositionMap,
  // Wall Access
  PrivacyPolicyId,
  // Wall Layout
  RemoveLicenseResponse,
  RosteringSettings,
  SamlConnectionApiResponse,
  ScheduledChangesResult,
  SchoolLibraryPermission,
  SchoolLibraryStudentPermission,
  SchoolLibraryTeacherPermission,
  Section,
  SectionAttributes,
  SharedCollection,
  SharedCollectionAccountKey,
  SnackbarLoadingState,
  SponsoredUser,
  StairstepPriceTier,
  StripeAddress,
  StripeInvoiceDetails,
  SwitchCurrencyResult,
  SwitchPlanResult,
  SyncStateUserIds,
  TeamLibraryMemberPermission,
  TeamLibraryPermission,
  TemplateFilters,
  Tenant,
  TenantAnalytics,
  TenantId,
  TenantInviteLinks,
  TenantInviteLinksParams,
  TenantParams,
  TenantToLibraryMigrationState,
  Theme,
  // Wall translations
  TranslatedComment,
  TranslatedCustomPostProperty,
  TranslatedEntity,
  TranslatedSection,
  TranslatedSelectionOption,
  TranslatedWall,
  TranslatedWish,
  // Verification Code
  UndoLibraryScheduledChangeResult,
  UndoScheduledChangeResult,
  UpdateDraftAttributes,
  UpdatePostArgs,
  UpdateUserGroupAttributes,
  UploadedFile,
  User,
  UserAchievements,
  UserAchievementsAnalyticsWallContributions,
  UserAchievementsDownloadBadge,
  UserAchievementsDownloadBadgeStatus,
  UserAchievementsDownloadCertificate,
  UserAchievementsDownloadCertificateStatus,
  UserCamelCase,
  UserFieldValue,
  // User follows
  UserFollowApiResponse,
  UserGroup,
  UserGroupDetailsApiResponse,
  UserGroupFolderApiResponse,
  UserGroupId,
  UserGroupMemberApiResponse,
  // Others
  UserGroupWallCollaborator,
  UserId,
  UserLibrarySetting,
  // Dashboard settings
  UserLibrarySettingsApiResponse,
  UserMentionSuggestion,
  // Mentions
  UserMentionSuggestionAttributes,
  UserOptions,
  UserWallCollaborator,
  UserWallCollaboratorInvite,
  UserWithHashId,
  VerificationCodePurpose,
  Voice,
  VoteOrRatingFieldValue,
  // Vue
  VueCssClass,
  Wall,
  WallAccessSettings,
  WallAccessSettingsToggles,
  // Wall analytics
  WallAnalytics,
  WallBackground,
  WallCamelCase,
  // Templates
  WallCreationFromTemplateResult,
  WallCustomPostProperty,
  // Wall language detection
  WallDetectedLanguage,
  // Wall follows
  WallFollowApiResponse,
  WallFollowInformation,
  WallGalleryOnboardingData,
  WallGalleryTemplate,
  // Wall Grading Settings
  WallGradingContextFile,
  WallGradingSettings,
  WallGroupAuthor,
  WallGroupColor,
  WallGroupNone,
  WallGroupPost,
  WallGroupSingleSelect,
  WallId,
  WallLayout,
  WallLayoutAttributes,
  WallLayoutPreviewImage,
  WallLinks,
  WallNameAvailability,
  WallOnboardingPanel,
  WallPostProperties,
  WallReactionData,
  WallReadTokenResponse,
  WallRemakeLink,
  WallRemakeLinkProps,
  // Wall RSS Feed
  WallRssFeedUrlResponse,
  WallSectionBreakoutLink,
  WallSubmissionRequestLink,
  WallSubmissionRequestProps,
  WallSuggestedTemplatesData,
  WallTemplate,
  WallViz,
  WallVizNameType,
  // Whiteboard
  WallWhiteboardRemakeLink,
  WallWhiteboardRemakeLinkProps,
  WallWishGroupBy,
  WallWishGroupByCustomProp,
  WhiteboardCardBackground,
  WhiteboardCardBackgroundsApiResponse,
  WhiteboardCardTemplate,
  WhiteboardCollaborationSettings,
  WhiteboardDefaultFrameBackground,
  WhiteboardDefaultFrameBackgroundApiResponse,
  WhiteboardSoundEffect,
  WhiteboardToolSettings,
  WishArrangement,
  WishAttachment,
  WishContent,
}
